"use client"

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

export const SEMANTIC_COLORS_STORAGE_KEY = "pos-semantic-colors"
export const SEMANTIC_COLORS_ATTR = "data-semantic-colors"

export function readSemanticColorsEnabled(): boolean {
  if (typeof window === "undefined") return false
  try {
    return localStorage.getItem(SEMANTIC_COLORS_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

export function applySemanticColors(enabled: boolean) {
  const root = document.documentElement
  if (enabled) {
    root.setAttribute(SEMANTIC_COLORS_ATTR, "on")
  } else {
    root.removeAttribute(SEMANTIC_COLORS_ATTR)
  }
}

interface SemanticColorsContextType {
  enabled: boolean
  setEnabled: (enabled: boolean) => void
}

const SemanticColorsContext = createContext<SemanticColorsContextType | undefined>(undefined)

export function SemanticColorsProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState(false)

  useEffect(() => {
    const initial = readSemanticColorsEnabled()
    setEnabledState(initial)
    applySemanticColors(initial)
  }, [])

  const setEnabled = useCallback((next: boolean) => {
    const value = next === true
    setEnabledState(value)
    try {
      localStorage.setItem(SEMANTIC_COLORS_STORAGE_KEY, String(value))
    } catch {
      // ignore quota / private mode
    }
    applySemanticColors(value)
  }, [])

  return (
    <SemanticColorsContext.Provider value={{ enabled, setEnabled }}>
      {children}
    </SemanticColorsContext.Provider>
  )
}

export function useSemanticColors() {
  const ctx = useContext(SemanticColorsContext)
  if (!ctx) throw new Error("useSemanticColors must be used within SemanticColorsProvider")
  return ctx
}
